import React, { forwardRef } from "react";
import { useControlContext } from "../../lib/control-context.js";
import { focusableDisabledProps } from "../../lib/focusable-disabled.js";
import { cn } from "../../lib/utils.js";
import { Slot } from "../../primitives/Slot.js";
import { Spinner } from "../Spinner/Spinner.js";
import type {
  ButtonAsChildProps as AsChild,
  ButtonBase,
  ButtonProps,
} from "./button-props.js";
import { railButtonClass } from "./button-rail.js";
import {
  buttonVariants,
  type ButtonVariantsOptions,
} from "./button-variants.js";

export type { ButtonAsChildProps, ButtonProps } from "./button-props.js";

/* Button — the API-standard reference atom (strategy §14.5, C7).
   CVA variants + forwardRef + rest-prop spread. Prop vocabulary is the
   system standard: `variant` (visual style), `size`. `iconOnly` renders a
   square icon button and is TYPED to require an `aria-label` so an icon-only
   control can never ship without an accessible name (§12). `loading` renders
   the Spinner atom (aria-hidden: no live region nests in the button), sets
   aria-busy, and blocks activation.

   Interaction grades are the generated utilities (ADR-0092): `focus-ring` is
   the one focus treatment and `press` the one pressed dip; every variant
   transitions the same property list. `primary` is the region's ONE commit
   button (invariant §9): a top-lit accent gradient with the rest glow, the
   hover glow and the pressed step. `soft` is the ACCENTED SECONDARY — the
   action related to the region's purpose but not its commit (Duplicate,
   Share, Launch from template). It is never the region's commit button and
   never a second accent beside `primary`; a region still carries exactly one
   `primary`. `inverse` is the dark-on-light action (no glow); it is not a
   primary, so it never counts as the region's accent.

   `tone` is the C7 semantic colour, typed per variant (ButtonVariantTones in
   button-variants.ts): ghost critical|ai (hover paint), outline critical,
   link neutral|ai, soft every tone, and primary ai. `primary` + `ai` is the
   iris CTA of the Player AI FAB and launcher ONLY (ADR-0076); every other
   commit stays accent. Omitted, every variant keeps today's look.

   `shape` is the corner (C7 words, lib/shape.ts): `rounded` (default,
   today's 8px), `pill` (fully round, width follows the content) and
   `circle` (fully round on the iconOnly square box). Omitted, no class
   changes. A boxed Button takes it on every variant and tone; the inline
   member takes none.

   Inside a WorkspaceControlGroup with `railControls`, a Button paints the
   chrome-control rail or primary look on the 32px box (button-rail.ts).
   Outside one, nothing changes.

   `asChild` (default false) paints the one child element, the host router's
   link, with the same buttonVariants classes through primitives/Slot.tsx and
   renders no <button>. The prop types live in button-props.ts. */

/* Two call signatures. The asChild form comes first, so a call site without
   asChild falls through to ButtonProps and is checked exactly as before (one
   union, no new members for TypeScript to split). ButtonProps comes last, so
   ComponentProps<typeof Button> stays ButtonProps. */
type ButtonComponent = ((
  props: AsChild & React.RefAttributes<HTMLAnchorElement>,
) => React.ReactElement | null) &
  React.ForwardRefExoticComponent<
    ButtonProps & React.RefAttributes<HTMLButtonElement>
  >;

export const Button = forwardRef<HTMLButtonElement, ButtonProps | AsChild>(
  function Button(props, ref) {
    const {
      variant,
      tone,
      size,
      className,
      children,
      loading,
      loadingText,
      disabled,
      type,
      iconOnly = false,
      shape,
      pressed,
      focusableWhenDisabled = false,
      onClick,
      "aria-pressed": ariaPressed,
      asChild = false,
      ...domProps
    } = props as Omit<ButtonBase, "asChild"> &
      ButtonVariantsOptions & { asChild?: boolean | undefined };
    const inactive = Boolean(disabled || loading);
    const rail = useControlContext() === "rail";
    const classOptions: ButtonVariantsOptions = {
      variant,
      tone,
      size,
      iconOnly,
      shape,
      pressed,
      focusableDisabled: inactive && focusableWhenDisabled,
    };
    const classes = cn(
      rail ? railButtonClass(classOptions) : buttonVariants(classOptions),
      className,
    );

    if (asChild) {
      return (
        <Slot ref={ref} className={classes} onClick={onClick} {...domProps}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        type={type ?? "button"}
        {...focusableDisabledProps({
          inactive,
          focusableWhenDisabled,
          onClick,
        })}
        aria-busy={loading || undefined}
        aria-pressed={pressed ?? ariaPressed}
        className={classes}
        {...domProps}
      >
        {loading && <Spinner variant="ring" size="sm" aria-hidden />}
        {loading && loadingText !== undefined ? loadingText : children}
      </button>
    );
  },
) as ButtonComponent;
